import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Button, Flex, Popover } from 'antd';

import { useAppSelector } from '../../../redux/hooks';
import { RootState } from 'redux/store';

import { useCallSocket } from 'hooks/useCallSocket';
import { useIntegrationsConfig } from 'hooks/integrations';
import useLiveActivityData from 'hooks/useLiveActivityData';
import { Loader, SearchBar, Typography } from '../../common';
import SelectTree from 'components/common/SelectTree';
import SortAndFilterMenu from 'components/common/SortAndFilterMenu/sort-and-filter-menu';
import AppliedFilters from './Filters/LiveCallsAppliedFilters';
import LiveCallsFilterMenu from './Filters/LiveCallsFilterMenu';
import ExtensionFiltersMenu, { ExtensionFilterFields } from './ExtensionFilters/ExtensionFiltersMenu';
import ExtensionsAppliedFilters from './ExtensionFilters/ExtensionsAppliedFilters';
import CallList from './CallList';
import ExtensionsList from './ExtensionsList';
import MiniCard from './MiniCard';
import DefaultLayout from '../../layout/DefaultLayout';
import TransferCallModal from './TransferModal/TransferCallModal';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import {
  AvailableUserListIcon,
  FilterIcon,
  IncomingCallsIcon,
  OutgoingCallsIcon,
  ParkedCallsIcon,
  TotalCallsIcon,
  TotalUsersIcon,
} from '../../../assets';

import { Utils } from 'utils';
import colors from '../../../styles/colors.module.scss';
import './liveCalls.scss';
import { Callee } from '../../../redux/slices/liveCalls.slice';

type MiniCardKey = 'incoming' | 'outgoing' | 'parked' | 'totalCalls' | 'totalUsers';

const MINI_CARDS_CONFIG: Array<{
  key: MiniCardKey;
  heading: string;
  iconClassName: string;
  containerBg: string;
  imageContainerBg: string;
}> = [
    {
      key: 'incoming',
      heading: 'Incoming',
      iconClassName: IncomingCallsIcon,
      containerBg: colors.lightGreen,
      imageContainerBg: colors.darkGreen,
    },
    {
      key: 'outgoing',
      heading: 'Outgoing',
      iconClassName: OutgoingCallsIcon,
      containerBg: colors.lightPurple,
      imageContainerBg: colors.darkPurple,
    },
    {
      key: 'parked',
      heading: 'Parked',
      iconClassName: ParkedCallsIcon,
      containerBg: colors.lightMaroon,
      imageContainerBg: colors.darkMaroon,
    },
    {
      key: 'totalCalls',
      heading: 'Total Calls',
      iconClassName: TotalCallsIcon,
      containerBg: colors.lightOrange,
      imageContainerBg: colors.darkOrange,
    },
    {
      key: 'totalUsers',
      heading: 'Total Users',
      iconClassName: TotalUsersIcon,
      containerBg: colors.lightCyan,
      imageContainerBg: colors.darkCyan,
    },
  ];

interface TransferCallBaseData {
  callUUID?: string;
  callees?: Callee[],
  callerIDNumber?: string,
  callerIDName?: string,
  destination?: string
}

const LiveCallsDefault = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isExtensionFilterOpen, setIsExtensionFilterOpen] = useState(false);
  const [localExtensionFilters, setLocalExtensionFilters] = useState<any>({});
  const initialFilters = {
    direction: null,
    extensions: null,
    callerIdNumber: null,
    phoneNumbers: null,
    callRecording: null,
    sortName: null,
    sortDuration: null,
    sortDirection: null,
    sortTime: null,
  } as const;
  const [filters, setFilters] = useState<any>({ ...initialFilters });

  const initialExtensionFilters = {
    status: null,
  } as const;

  const [extensionFilters, setExtensionFilters] = useState<any>({ ...initialExtensionFilters });

  const renderCountRef = useRef(0);
  const prevIntegrationConfigRefForReference = useRef<any>();
  const prevIntegrationConfigRef = useRef<any>();
  const prevCallsRef = useRef<any>();
  const prevExtensionRef = useRef<any>();
  const prevAppsLoadingRef = useRef<any>();
  const prevLiveActivityDataRef = useRef<any>();
  const prevSortingRef = useRef<any>();

  const calls = useAppSelector((state: RootState) => state?.liveCalls?.calls);
  const extension = useAppSelector((state: RootState) => state?.apps.data?.extension);
  const appsLoading = useAppSelector((state: RootState) => state?.apps.loading);

  const { handleHangUp, handleRecord, handleTransfer } = useCallSocket();

  const [transferCallData, setTransferCallData] = useState<TransferCallBaseData & {
    selectedCallExt: string,
    selectedCallDestination: string,
  } | null>(null);
  const [transferParticipantData, setTransferParticipantData] = useState<TransferCallBaseData & {
    targetExt: string,
  } | null>(null);
  const selectedCallUUID = useMemo(() => transferCallData?.callUUID, [transferCallData?.callUUID])

  const integrationConfig = useIntegrationsConfig();
  const { incoming, outgoing, parked, totalCalls, totalUsers } = useLiveActivityData(calls, extension);

  const [extensionSortOrder, setExtensionSortOrder] = useState<'asc' | 'desc'>('asc');
  const [extensionSortType, setExtensionSortType] = useState<'name' | 'extension' | 'status'>('name');

  const stableIntegrationConfig = useMemo(() => integrationConfig, [
    JSON.stringify(integrationConfig)
  ]);

  const isDataReady = useMemo(() => {
    return !appsLoading && extension && Array.isArray(extension) && extension.length >= 0;
  }, [appsLoading, extension]);

  const miniCardsData = useMemo(() => {
    const values: Record<MiniCardKey, number> = { incoming, outgoing, parked, totalCalls, totalUsers };
    return MINI_CARDS_CONFIG.map(cfg => ({
      ...cfg,
      value: values[cfg.key].toString(),
    }));
  }, [incoming, outgoing, parked, totalCalls, totalUsers]);

  renderCountRef.current += 1;

  if (prevIntegrationConfigRefForReference.current !== stableIntegrationConfig) {
    prevIntegrationConfigRefForReference.current = stableIntegrationConfig;
  }

  if (prevIntegrationConfigRef.current !== stableIntegrationConfig) {
    prevIntegrationConfigRef.current = stableIntegrationConfig;
  }

  if (prevCallsRef.current !== calls) {
    prevCallsRef.current = calls;
  }

  if (prevExtensionRef.current !== extension) {
    prevExtensionRef.current = extension;
  }

  if (prevAppsLoadingRef.current !== appsLoading) {
    prevAppsLoadingRef.current = appsLoading;
  }

  const currentLiveActivityData = { incoming, outgoing, parked, totalCalls, totalUsers };
  if (JSON.stringify(prevLiveActivityDataRef.current) !== JSON.stringify(currentLiveActivityData)) {
    prevLiveActivityDataRef.current = currentLiveActivityData;
  }

  const currentSorting = { extensionSortOrder, extensionSortType };
  if (JSON.stringify(prevSortingRef.current) !== JSON.stringify(currentSorting)) {
    prevSortingRef.current = currentSorting;
  }

  useEffect(() => {
    if (isDataReady && !isInitialized) {
      const timer = setTimeout(() => {
        setIsInitialized(true);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isDataReady, isInitialized]);



  const { extensionsOptions, numbersOptions } = useMemo(() => {
    const numbersSet = new Set<string>();

    (calls || []).forEach((c: any) => {
      const dialedNumber = c.dialedNumber;
      if (dialedNumber && c.callDirection === 'inbound' && dialedNumber.length >= 6) {
        numbersSet.add(Utils.formatPhoneNumber(dialedNumber));
      }
    });

    return {
      extensionsOptions: extension?.map(({ name, extension }) => ({ value: extension, label: `${name || ''} (${extension})` })),
      numbersOptions: Array.from(numbersSet)
    };
  }, [calls, extension]);

  const handleApplyFilters = useCallback((f: any) => {
    setFilters(f);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({ ...initialFilters });
  }, []);

  const handleRemoveFilter = useCallback((key: string) => {
    setFilters((prev: any) => ({ ...prev, [key]: null }));
  }, []);

  const handleApplyExtensionFilters = useCallback((f: any) => {
    setExtensionFilters(f);
  }, []);

  const handleClearExtensionFilters = useCallback(() => {
    setExtensionFilters({ ...initialExtensionFilters });
  }, []);

  const handleRemoveExtensionFilter = useCallback((key: string) => {
    setExtensionFilters((prev: any) => ({ ...prev, [key]: null }));
  }, []);

  useEffect(() => {
    setLocalExtensionFilters(extensionFilters || {});
  }, [extensionFilters]);

  const handleExtensionFilterChange = useCallback((fieldName: string, value: any) => {
    setLocalExtensionFilters((prev: any) => ({ ...prev, [fieldName]: value }));
  }, []);

  const handleApplyExtensionFiltersFromMenu = useCallback(() => {
    setExtensionFilters(localExtensionFilters);
  }, [localExtensionFilters]);

  const handleClearExtensionFiltersFromMenu = useCallback(() => {
    const cleared = Object.keys(localExtensionFilters || {}).reduce((acc: any, key: string) => {
      acc[key] = null;
      return acc;
    }, {});
    setLocalExtensionFilters(cleared);
    setExtensionFilters(cleared);
  }, [localExtensionFilters]);

  const handleExtensionTreeChange = useCallback((value: string | undefined) => {
    if (!value) {
      setExtensionSortOrder('asc');
      setExtensionSortType('name');
      return;
    }

    if (value === 'name-asc') {
      setExtensionSortOrder('asc');
      setExtensionSortType('name');
    } else if (value === 'name-desc') {
      setExtensionSortOrder('desc');
      setExtensionSortType('name');
    } else if (value === 'extension-asc') {
      setExtensionSortOrder('asc');
      setExtensionSortType('extension');
    } else if (value === 'extension-desc') {
      setExtensionSortOrder('desc');
      setExtensionSortType('extension');
    } else if (value === 'status') {
      setExtensionSortOrder('asc');
      setExtensionSortType('status');
    }
  }, []);

  const extensionTreeValue = useMemo(() => {
    if (extensionSortType === 'name') {
      return extensionSortOrder === 'asc' ? 'name-asc' : 'name-desc';
    } else if (extensionSortType === 'extension') {
      return extensionSortOrder === 'asc' ? 'extension-asc' : 'extension-desc';
    } else if (extensionSortType === 'status') {
      return 'status';
    }
    return undefined;
  }, [extensionSortOrder, extensionSortType]);

  const extensionTreeData = useMemo(() => [
    {
      title: <span className="select-tree-parent">Name</span>,
      value: 'name',
      key: 'name',
      selectable: false,
      children: [
        { title: 'Ascending (Name)', value: 'name-asc', key: 'name-asc' },
        { title: 'Descending (Name)', value: 'name-desc', key: 'name-desc' },
      ],
    },
    {
      title: <span className="select-tree-parent">Extension</span>,
      value: 'extension',
      key: 'extension',
      selectable: false,
      children: [
        { title: 'Ascending (Extension)', value: 'extension-asc', key: 'extension-asc' },
        { title: 'Descending (Extension)', value: 'extension-desc', key: 'extension-desc' },
      ],
    },
    {
      title: 'By Status',
      value: 'status',
      key: 'status',
    },
  ], []);

  const processedCalls = useMemo(() => {
    let list: any[] = Array.isArray(calls) ? [...calls] : [];

    if (filters.direction) {
      list = list.filter((c) => {
        const directionMap: { [key: string]: string } = {
          'incoming': 'inbound',
          'outgoing': 'outbound',
          'local': 'local'
        };
        const expectedDirection = directionMap[filters.direction] || filters.direction;
        return c.callDirection === expectedDirection;
      });
    }

    if (filters.extensions && Array.isArray(filters.extensions) && filters.extensions.length) {
      list = list.filter((c) => {
        return filters.extensions.includes(c?.callerIDNumber)
          || false;
      });
    }

    if (filters.callerIdNumber) {
      const query = String(filters.callerIdNumber).toLowerCase();
      list = list.filter((c) => String(c.callerIDNumber || '').toLowerCase().includes(query));
    }

    if (filters.phoneNumbers && Array.isArray(filters.phoneNumbers) && filters.phoneNumbers.length) {
      list = list.filter((c) => {
        const dialedNumber = Utils.formatPhoneNumber(c.dialedNumber || '');
        return filters.phoneNumbers.includes(dialedNumber);
      });
    }

    if (filters.callRecording !== null) {
      const want = filters.callRecording === 'yes';
      list = list.filter((c) => Boolean(c.isRecording) === want);
    }

    if (filters.sortName) {
      list.sort((a, b) => {
        const aN = String(a.callerIDName || '');
        const bN = String(b.callerIDName || '');
        return filters.sortName === 'name-asc' ? aN.localeCompare(bN) : bN.localeCompare(aN);
      });
    }

    if (filters.sortDuration) {
      list.sort((a, b) => {
        const now = Date.now();
        const startTimeA = new Date(a.startTime || 0).getTime();
        const startTimeB = new Date(b.startTime || 0).getTime();
        const durA = now - startTimeA;
        const durB = now - startTimeB;
        return filters.sortDuration === 'duration-asc' ? durA - durB : durB - durA;
      });
    }

    if (filters.sortDirection) {
      const incomingFirst = filters.sortDirection === "direction-incoming";
      list.sort((a, b) => {
        const da = a.callDirection || "";
        const db = b.callDirection || "";

        const rank = (d: string) => {
          if (d === "inbound") return incomingFirst ? 0 : 1;
          if (d === "outbound") return incomingFirst ? 1 : 0;
          if (d === "local") return 2;
          return 3;
        };

        return rank(da) - rank(db);
      });
    }

    if (filters.sortTime) {
      list.sort((a, b) => {
        const ta = new Date(a.startTime || 0).getTime();
        const tb = new Date(b.startTime || 0).getTime();
        return filters.sortTime === 'time-asc' ? ta - tb : tb - ta;
      });
    }

    return list;
  }, [calls, filters]);

  const onRecord = useCallback((UUID: string, action: "start" | "stop") => {
    handleRecord(UUID, action)
  }, [handleRecord])

  const onTransfer = useCallback((ext: string, calleeUUID?: string) => {
    if (selectedCallUUID) {
      if (calleeUUID) {
        handleTransfer(calleeUUID, ext);
      } else {
        handleTransfer(selectedCallUUID, ext);
      }
    }

    setTransferCallData(null)
  }, [transferCallData, handleTransfer])

  const openTransferModal = useCallback((
    callUUID: string,
    selectedCallExt: string,
    selectedCallDestination: string,
    callees?: Callee[],
    callerIDNumber?: string,
    callerIDName?: string
  ) => {
    setTransferCallData({
      callUUID,
      selectedCallExt,
      selectedCallDestination,
      callees,
      callerIDNumber,
      callerIDName
    });
  }, []);

  const onDndTransfer = useCallback((
    callUUID: string,
    targetExt: string,
    callees: Callee[],
    callerIDNumber: string,
    callerIDName: string,
    destination: string) => {
    setTransferParticipantData({
      callUUID,
      targetExt,
      callees,
      callerIDNumber,
      callerIDName,
      destination
    });
  }, [])

  const closeTransferModal = useCallback(() => {
    setTransferCallData(null);
  }, []);

  const onTransferParticipant = useCallback((calleeUUID: string) => {
    if (transferParticipantData) {
      handleTransfer(calleeUUID, transferParticipantData.targetExt);
      setTransferParticipantData(null);
    }
  }, [handleTransfer, transferParticipantData]);

  const closeTransferParticipantModal = useCallback(() => {
    setTransferParticipantData(null);
  }, []);

  if (!isInitialized) {
    return (
      <DefaultLayout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'transparent'
        }}>
          <Loader />
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <DndProvider backend={HTML5Backend}>
        <div className="live-calls-container">
          <div className="live-calls-body-container">
            <div className="mini-cards-container">
              {miniCardsData.map(({ key, ...rest }) => <MiniCard key={key} {...rest} />)}
            </div>

            <div className="user-list-active-calls-container">
              <div className="users-list-container">
                <Flex justify="space-between" align="center">
                  <div className="user-list-heading">
                    <img src={AvailableUserListIcon} alt="Available user list icon" />
                    <Typography variant="h3-bold">Available User Online</Typography>
                  </div>

                </Flex>
                  <SortAndFilterMenu
                    style={{ width: '100%', paddingInline: '1rem' }}
                    showLabel={true}
                    treeStyle={{ width: '100%' }}
                    allowClearTree={false}
                    treeData={extensionTreeData}
                    treeValue={extensionTreeValue}
                    handleTreeChange={handleExtensionTreeChange}
                    onApplyFilters={handleApplyExtensionFiltersFromMenu}
                    onClear={handleClearExtensionFiltersFromMenu}
                  >
                    <ExtensionFilterFields
                      localFilters={localExtensionFilters}
                      handleFilterChange={handleExtensionFilterChange}
                    />
                  </SortAndFilterMenu>

                {extensionFilters?.status && <div style={{ paddingInline: '1.6rem' }}>
                  <ExtensionsAppliedFilters
                    filters={extensionFilters}
                    handleClearFilters={handleClearExtensionFilters}
                    handleRemoveFilter={handleRemoveExtensionFilter}
                  />
                </div>}

                <div className="searchBar">
                  <SearchBar value={agentSearch} onChange={(e) => setAgentSearch(e.target.value)} />
                </div>
                <ul className="list-container" role="list">
                  <ExtensionsList
                    sortOrder={extensionSortOrder}
                    sortType={extensionSortType}
                    extensionSearch={agentSearch}
                    filters={extensionFilters}
                    onDropCallToExtension={onDndTransfer}
                  />
                </ul>
              </div>

              <div className="active-calls-container">
                <div className="active-calls-header">
                  <div className="sort-container">
                    <span className='sort-text'>  Sort By: </span>
                    <SelectTree
                      style={{ width: '175px' }}
                      className='call-center-sort-select'
                      treeData={[{
                        title: <span className="select-tree-parent">Caller ID Name</span>,
                        value: 'name',
                        key: 'name',
                        selectable: false,
                        children: [
                          { title: 'A → Z', value: 'name-asc', key: 'name-asc' },
                          { title: 'Z → A', value: 'name-desc', key: 'name-desc' },
                        ],
                      },
                      {
                        title: <span className="select-tree-parent">Call Duration</span>,
                        value: 'duration',
                        key: 'duration',
                        selectable: false,
                        children: [
                          { title: 'Shortest → Longest', value: 'duration-asc', key: 'duration-asc' },
                          { title: 'Longest → Shortest', value: 'duration-desc', key: 'duration-desc' },
                        ],
                      },
                      {
                        title: <span className="select-tree-parent">Direction</span>,
                        value: 'direction',
                        key: 'direction',
                        selectable: false,
                        children: [
                          { title: 'Incoming First', value: 'direction-incoming', key: 'direction-incoming' },
                          { title: 'Outgoing First', value: 'direction-outgoing', key: 'direction-outgoing' },
                          { title: 'Local First', value: 'direction-local', key: 'direction-local' },
                        ],
                      },
                      {
                        title: <span className="select-tree-parent">Time</span>,
                        value: 'time',
                        key: 'time',
                        selectable: false,
                        children: [
                          { title: 'Newest First', value: 'time-desc', key: 'time-desc' },
                          { title: 'Oldest First', value: 'time-asc', key: 'time-asc' },
                        ],
                      }]}
                      value={(filters.sortName || filters.sortDuration || filters.sortDirection || filters.sortTime) as any}
                      onChange={(value) => {
                        const update: any = { sortName: null, sortDuration: null, sortDirection: null, sortTime: null }
                        if (value?.startsWith('name-')) update.sortName = value
                        else if (value?.startsWith('duration-')) update.sortDuration = value
                        else if (value?.startsWith('direction-')) update.sortDirection = value
                        else if (value?.startsWith('time-')) update.sortTime = value
                        setFilters((prev: any) => ({ ...prev, ...update }))
                      }}
                      placeholder="Default"
                    />
                  </div>
                  <Popover
                    placement="bottomRight"
                    trigger="click"
                    open={isFilterOpen}
                    onOpenChange={(open) => setIsFilterOpen(open)}
                    styles={{ body: { padding: 0 } }}
                    content={<LiveCallsFilterMenu onClose={() => setIsFilterOpen(false)} numbersOptions={numbersOptions} extensionsOptions={extensionsOptions} onApplyFilters={handleApplyFilters} currentFilters={filters} />}
                  >
                    <div onClick={() => setIsFilterOpen(true)} style={{ cursor: 'pointer' }} className="filter-bar-container">
                      <img src={FilterIcon} alt="Filter icon" />
                      <div>
                        <Typography variant="text-2">Filters</Typography>
                      </div>
                    </div>
                  </Popover>
                </div>

                <AppliedFilters filters={filters} handleClearFilters={handleClearFilters} handleRemoveFilter={handleRemoveFilter} />

                <CallList
                  calls={processedCalls || []}
                  onRecord={onRecord}
                  onHangUp={handleHangUp}
                  onClickCall={(call) => { }}
                  onTransfer={openTransferModal}
                  onDndTransfer={onDndTransfer}
                />
              </div>
            </div>
          </div>
        </div>
        {transferCallData && (
          <TransferCallModal
            mode="regular"
            excludeExts={[transferCallData?.selectedCallExt || '', transferCallData?.selectedCallDestination || '']}
            open={!!transferCallData}
            callUUID={selectedCallUUID || ''}
            onClose={closeTransferModal}
            onTransfer={onTransfer}
            callees={transferCallData?.callees || []}
            callerIDNumber={transferCallData?.callerIDNumber || ''}
            callerIDName={transferCallData?.callerIDName || ''}
            destination={transferCallData?.selectedCallDestination || ''}
          />
        )}
        {transferParticipantData && (
          <TransferCallModal
            mode="dnd"
            destination={transferParticipantData?.destination || ''}
            callerIDNumber={transferParticipantData?.callerIDNumber || ''}
            callerIDName={transferParticipantData?.callerIDName || ''}
            open={!!transferParticipantData}
            onClose={closeTransferParticipantModal}
            onConfirmTransfer={onTransferParticipant}
            callees={transferParticipantData?.callees || []}
            callUUID={transferParticipantData?.callUUID || ''}
          />
        )}
      </DndProvider>
    </DefaultLayout>
  );
};

export default LiveCallsDefault;
